import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import type { DateRangeOption } from "@/components/clicks/DateRangeFilter";
import { parseUrlActiveFilters } from "@/lib/url-active-filters";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import {
  clicksService,
  type DomainPagesParams,
  type ClicksStatsResult,
  type ClicksTrendGranularity,
} from "@/services/ClicksService";
import { gscService } from "@/services/GscService";
import { BRAND_DOMAIN_KEYS, BRAND_DOMAIN_READY } from "@/config/dimensions";

/** Same set the Dashboard offers, so the two tables behave alike. */
export const CLICKS_LIMIT_OPTIONS = [15, 30, 50, 100] as const;
export type ClicksLimit = (typeof CLICKS_LIMIT_OPTIONS)[number];
export const CLICKS_PAGE_SIZE: ClicksLimit = 15;

/** Anything not in the list falls back to the default - the value comes from the URL. */
export const normalizeClicksLimit = (value: number): ClicksLimit =>
  (CLICKS_LIMIT_OPTIONS as readonly number[]).includes(value)
    ? (value as ClicksLimit)
    : CLICKS_PAGE_SIZE;
export const CLICKS_STATS_STALE_TIME = 1000 * 60 * 5;

/**
 * Milliseconds until the next hh:03. clicks_daily is recomputed at hh:00
 * (migration 20261008120000) and takes 1-2 minutes, so a page left open picks
 * up the new numbers at hh:03 instead of showing the last hour until a reload.
 * Used as refetchInterval: re-read after every fetch, and paused while the tab
 * is hidden (react-query's default), so it is one request per hour per open tab.
 * In UTC, like the cron: local time skipped an hour at a DST switch and would
 * be half an hour off in a +5:30 zone.
 */
export const untilClicksRefresh = (now: Date = new Date()): number => {
  const next = new Date(now);
  next.setUTCMinutes(3, 0, 0);
  if (next <= now) next.setUTCHours(next.getUTCHours() + 1);
  return next.getTime() - now.getTime();
};

export type ClicksParams = {
  page: number;
  limit: ClicksLimit;
  /** The primary grouping dimension (groupDims[0]) — kept for the many callers
   *  that only need one (drill-down, GSC merge, expansion). */
  groupBy: string;
  /** All grouping dimensions, in order. One = normal; two+ = multi-group. */
  groupDims: string[];
  dateRange: DateRangeOption;
  compareEnabled: boolean;
  customFrom?: string;
  customTo?: string;
  activeFilters: ReturnType<typeof parseUrlActiveFilters>;
};

export type ClicksLoaderData = ClicksParams;

export const EMPTY_CLICKS_STATS: ClicksStatsResult = {
  data: [],
  previousData: null,
  totalCount: 0,
};

export const parseClicksParamsFromUrl = (url: URL): ClicksParams => {
  const pageParam = Number(url.searchParams.get("page") || "1");
  const page =
    Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;

  // A bookmark or an old tab can still carry groupBy=brand or a brand filter.
  // Until the matview has those columns that means a 500, so fall back.
  const applyReady = (g: string): string =>
    !BRAND_DOMAIN_READY && BRAND_DOMAIN_KEYS.has(g) ? "campaign" : g;
  const rawGroupBy = url.searchParams.get("groupBy") || "domain";
  // groupDims (comma list) is the multi-group source; groupBy is its first entry.
  const rawDims = url.searchParams.get("groupDims");
  const groupDims = Array.from(
    new Set(
      (rawDims
        ? rawDims.split(",").map((s) => s.trim()).filter(Boolean)
        : [rawGroupBy]
      ).map(applyReady),
    ),
  );
  const groupBy = groupDims[0] ?? "domain";

  const parsedFilters = parseUrlActiveFilters(url.searchParams.get("filters"));
  const activeFilters = BRAND_DOMAIN_READY
    ? parsedFilters
    : Object.fromEntries(
        Object.entries(parsedFilters).filter(
          ([key]) => !BRAND_DOMAIN_KEYS.has(key),
        ),
      );

  return {
    page,
    limit: normalizeClicksLimit(Number(url.searchParams.get("limit") || CLICKS_PAGE_SIZE)),
    groupBy,
    groupDims,
    dateRange: (url.searchParams.get("dateRange") ||
      "last_7_days") as DateRangeOption,
    compareEnabled: url.searchParams.get("compare") === "true",
    customFrom: url.searchParams.get("from") || undefined,
    customTo: url.searchParams.get("to") || undefined,
    activeFilters,
  };
};

// Key deliberately omits page and limit: the grid pages the full result in the
// browser, so changing page or page size must not refetch.
export const getClicksAllStatsQueryKey = (params: ClicksParams) =>
  [
    "clicks",
    "all-stats",
    {
      groupBy: params.groupBy,
      groupDims: params.groupDims,
      dateRange: params.dateRange,
      compareEnabled: params.compareEnabled,
      customFrom: params.customFrom,
      customTo: params.customTo,
      activeFilters: params.activeFilters,
    },
  ] as const;

export const fetchClicksAllStats = (params: ClicksParams) =>
  clicksService.getAllStats(params);

export const useClicksAllStats = (params: ClicksParams) =>
  useQuery({
    queryKey: getClicksAllStatsQueryKey(params),
    queryFn: () => fetchClicksAllStats(params),
    placeholderData: keepPreviousData,
    staleTime: CLICKS_STATS_STALE_TIME,
    refetchInterval: () => untilClicksRefresh(),
    // No retry: a failure here is a timeout of a multi-second aggregation, and
    // retrying it at once doubles the load that caused it (08.10.2026).
    retry: false,
    // Fail soft: a heavy view (wide grouping + all-time + big filter) can exceed
    // the 8s DB statement timeout and 500. Don't let that blow up the whole page
    // via the error boundary — surface it inline so the controls stay usable and
    // the user can narrow the query.
    throwOnError: false,
  });

/**
 * Domain key shared by both sources for the GSC↔Keitaro merge. Mirrors the SQL
 * `normalize_url` host rule (lowercase, no protocol/www, host only) closely
 * enough to match: Keitaro's domain grouping already returns normalize_url
 * output, and GSC domains are bare hosts.
 */
export const normDomainKey = (v: string): string =>
  (v || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "");

export type GscDomainMetrics = {
  gsc_clicks: number;
  gsc_impressions: number;
  gsc_ctr: number;
  gsc_position: number;
};

/**
 * GSC metrics per domain for the current date range, keyed by normDomainKey,
 * for merging into the Clicks grid. Only fires when grouping by domain (GSC has
 * no campaign/stream/offer dimension). Never throws — GSC covers few domains.
 */
export const useGscDomainMetrics = (params: ClicksParams) =>
  useQuery({
    queryKey: [
      "clicks",
      "gsc-domains",
      {
        dateRange: params.dateRange,
        customFrom: params.customFrom,
        customTo: params.customTo,
      },
    ] as const,
    queryFn: async () => {
      const { current } = getDateRangeDates(
        params.dateRange,
        new Date(),
        params.customFrom,
        params.customTo,
      );
      const rows = await gscService.getDomainMetricsForRange(
        current.start,
        current.end,
      );
      const map = new Map<string, GscDomainMetrics>();
      for (const r of rows) {
        map.set(normDomainKey(r.domain), {
          gsc_clicks: r.clicks,
          gsc_impressions: r.impressions,
          gsc_ctr: r.ctr,
          gsc_position: r.position,
        });
      }
      return map;
    },
    enabled: params.groupBy === "domain",
    staleTime: CLICKS_STATS_STALE_TIME,
    throwOnError: false,
  });

/**
 * Latest date GSC has data for, for the freshness badge. Slow-moving, so a long
 * stale time; fail-soft.
 */
export const useGscFreshness = () =>
  useQuery({
    queryKey: ["clicks", "gsc-freshness"] as const,
    queryFn: () => gscService.getFreshness(),
    staleTime: 1000 * 60 * 30,
    throwOnError: false,
  });

/**
 * Daily/weekly/monthly Keitaro trend for the chart. Independent of grouping and
 * paging (it is daily totals over the current dates + filters). Fail-soft: the
 * get_clicks_trend RPC may not be deployed yet.
 */
export const useClicksTrend = (params: {
  activeFilters: ClicksParams["activeFilters"];
  granularity: ClicksTrendGranularity;
  dateRange: ClicksParams["dateRange"];
  customFrom?: string;
  customTo?: string;
}) =>
  useQuery({
    queryKey: ["clicks", "trend", params] as const,
    queryFn: () => clicksService.getClicksTrend(params),
    placeholderData: keepPreviousData,
    staleTime: CLICKS_STATS_STALE_TIME,
    refetchInterval: () => untilClicksRefresh(),
    throwOnError: false,
  });

/**
 * One trend per selected dimension value, for the chart's "compare" overlay:
 * each checked row (domain, campaign…) becomes its own line. One query per value
 * (capped upstream at 5), each the normal trend with that value added as an
 * include-filter — so the lines add up to what the table shows.
 */
export const useClicksCompareTrends = (
  values: string[],
  // The filter overlay that scopes the trend to one checked row. For a single
  // grouping it is {domain: [value]}; for a composite row ("GB ▸ Drip Casino")
  // it is one include-filter per grouped dimension. Caller builds it so this hook
  // stays dimension-agnostic.
  makeFilters: (value: string) => ClicksParams["activeFilters"],
  base: {
    activeFilters: ClicksParams["activeFilters"];
    granularity: ClicksTrendGranularity;
    dateRange: ClicksParams["dateRange"];
    customFrom?: string;
    customTo?: string;
  },
) =>
  useQueries({
    queries: values.map((value) => {
      const overlay = makeFilters(value);
      return {
        queryKey: ["clicks", "trend-compare", { value, overlay, ...base }] as const,
        queryFn: () =>
          clicksService.getClicksTrend({
            ...base,
            activeFilters: { ...base.activeFilters, ...overlay },
          }),
        placeholderData: keepPreviousData,
        staleTime: CLICKS_STATS_STALE_TIME,
        refetchInterval: () => untilClicksRefresh(),
        throwOnError: false,
      };
    }),
  });

/**
 * GSC trend for the chart (organic search), aggregated across domains. Only
 * fetched when a GSC metric is actually plotted — `enabled` gates the query so
 * it costs nothing when the chart shows Keitaro metrics alone.
 */
export const useGscChartTrend = (
  params: {
    granularity: ClicksTrendGranularity;
    dateRange: ClicksParams["dateRange"];
    customFrom?: string;
    customTo?: string;
    domain?: string;
  },
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["clicks", "gsc-trend", params] as const,
    queryFn: () => clicksService.getGscTrend(params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: CLICKS_STATS_STALE_TIME,
    throwOnError: false,
  });

/**
 * The URL rows under one domain, as full CampaignStat rows so they render under
 * the same columns as the parent. Disabled until the row is actually open, so a
 * table of 15 domains does not fire 15 queries against the click log.
 */
export const useDomainPages = (
  params: DomainPagesParams,
  enabled: boolean,
) =>
  useQuery({
    // activeFilters is part of the key, not just the request: two rows for the
    // same domain under different sidebar filters are different lists.
    queryKey: ["clicks", "domain-pages", params] as const,
    queryFn: () => clicksService.getDomainPages(params),
    enabled,
    staleTime: CLICKS_STATS_STALE_TIME,
    // The client sets throwOnError globally so a failed main query reaches the
    // route error boundary. That is right for the table and wrong here: this
    // is one expandable row, and losing it must not replace the whole page
    // with a 500. Measured 22.09.2026 - it did exactly that.
    throwOnError: false,
  });

/**
 * How fresh the pipeline is. Refetched on an interval because the answer
 * changes without anything on the page changing — the sync runs every 4 hours
 * and a tab can stay open much longer than that.
 */
export const usePipelineFreshness = () =>
  useQuery({
    queryKey: ["clicks", "freshness"] as const,
    queryFn: () => clicksService.getPipelineFreshness(),
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
    // Same reason as above, and more so: a badge strip is decoration. The page
    // has to render without it.
    throwOnError: false,
  });
